<template>
  <component :is="tag" :class="classes" v-bind="$attrs">
    <template v-if="title">
      {{ title }}
    </template>

    <slot v-else />
  </component>
</template>

<script setup lang="ts">
import { provide, inject, computed, onMounted, ref } from 'vue'
import { useDisplay } from 'vuetify'

defineOptions({ name: 'BaseHeading' })

const props = defineProps({
  align: { type: String },
  dense: { type: Boolean },
  size: { type: String, default: 'text-h3' },
  space: { type: [Number, String], default: 4 },
  mobileSize: { type: String, default: 'text-h4' },
  mobileBreakpoint: { type: [Number, String], default: 768 },
  tag: { type: String, default: 'h1' },
  title: { type: String },
  weight: { type: String, default: 'black' },
  isDark: Boolean,
})

const theme = inject('theme', { isDark: false })
const heading = inject('heading', { align: 'left' })

// Default for props.align
const effectiveAlign = computed(() => props.align ?? heading.align)

provide('heading', { align: effectiveAlign })

// used to solve client- vs. server-rendering conflicts
const isClient = ref(false)
onMounted(() => {
  isClient.value = true
})

const { mdAndUp } = useDisplay()
const fontSize = computed(() => {
  if (!isClient.value) {
    return props.size
  }
  return mdAndUp.value ? props.size : props.mobileSize
})

const classes = computed(() => [
  fontSize.value,
  `font-weight-${props.weight}`,
  `mb-${props.space}`,
  `text-${effectiveAlign.value}`,
  theme.isDark && 'base-heading--on-dark',
])
</script>

<style scoped>
.base-heading--on-dark {
  color: white;
}
</style>
